import React, { FC, ReactNode } from "react";
import { Breadcrumb, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Head from "../../../layout/head/Head";
import "../projects/hub/hub.css";
import "./project.css";

// Resolves the current project from router state with Redux fallback —
// the same precedence LayoutProject/ProjectSidebar use. Survives hard refresh.
export const useProjectContext = () => {
  const location = useLocation();
  const { currentProjectId, currentProjectName } = useSelector(
    (state: any) => state.layout
  );
  const locationState = location.state as any;
  const stateProjectId =
    locationState?.id && !isNaN(Number(locationState.id))
      ? locationState.id
      : locationState?.projectId && !isNaN(Number(locationState.projectId))
      ? locationState.projectId
      : null;

  return {
    projectId: stateProjectId || currentProjectId,
    projectName: locationState?.projectName || currentProjectName || "Project",
  };
};

export const BackButton: FC<{ onClick: () => void; title?: string }> = ({
  onClick,
  title = "Back",
}) => (
  <button
    type="button"
    className="pms-back-btn"
    onClick={onClick}
    title={title}
    aria-label={title}
  >
    <ArrowLeftOutlined style={{ fontSize: 13 }} />
  </button>
);

export interface Crumb {
  label: ReactNode;
  onClick?: () => void;
}

interface ProjectPageShellProps {
  headTitle: string;
  breadcrumb: Crumb[];
  title?: ReactNode;
  eyebrow?: string;
  onBack?: () => void;
  actions?: ReactNode;
  stats?: ReactNode;
  tabs?: ReactNode;
  toolbar?: ReactNode;
  stickyToolbar?: boolean;
  loading?: boolean;
  children: ReactNode;
}

// The in-project page frame: breadcrumb + ink title + actions, optional stat
// strip / tabs / (sticky) toolbar, then a NATURAL-FLOW body. The LayoutProject
// Content pane is the single scroll container — never add fixed heights or
// inner overflow containers inside a page.
// NOTE: modals/drawers must be mounted as SIBLINGS of this shell so the
// `loading` state can never unmount them mid-flow (staged uploads depend on it).
const ProjectPageShell: FC<ProjectPageShellProps> = ({
  headTitle,
  breadcrumb,
  title,
  eyebrow,
  onBack,
  actions,
  stats,
  tabs,
  toolbar,
  stickyToolbar = true,
  loading = false,
  children,
}) => {
  const lastCrumb = breadcrumb[breadcrumb.length - 1];

  return (
    <>
      <Head title={headTitle} />
      <div className="pms-page">
        {/* Single compact row: back + eyebrow/title left, breadcrumb + actions right */}
        <div className="pms-page-header">
          <div className="pms-header-row">
            <div className="pms-header-left">
              {onBack && <BackButton onClick={onBack} />}
              <div className="pms-header-titles">
                {eyebrow && <div className="pms-eyebrow">{eyebrow}</div>}
                <h2 className="pms-page-title">{title ?? lastCrumb?.label}</h2>
              </div>
            </div>
            <div className="pms-header-right">
              <Breadcrumb
                items={breadcrumb.map((c, i) => ({
                  title: c.onClick ? (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        c.onClick?.();
                      }}
                    >
                      {c.label}
                    </a>
                  ) : (
                    c.label
                  ),
                  key: i,
                }))}
              />
              {actions && <div className="pms-page-actions">{actions}</div>}
            </div>
          </div>
        </div>

        {stats && <div className="pms-stat-strip">{stats}</div>}
        {tabs}
        {toolbar && (
          <div
            className={`pms-toolbar${
              stickyToolbar ? " pms-toolbar--sticky" : ""
            }`}
          >
            {toolbar}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          children
        )}
      </div>
    </>
  );
};

export default ProjectPageShell;
